/* Real Shopify cart lines. Fulfillment eligibility remains a merchant-side manual check. */
(() => {
  'use strict';
  const node = document.getElementById('MFVSurpriseConfig');
  if (!node || window.MFVSurpriseCart || !window.MFVGiftRules) return;
  const config = JSON.parse(node.textContent);
  const rawFetch = window.fetch.bind(window);
  const root = () => window.Shopify?.routes?.root || '/';
  const giftIds = config.giftVariants.map(String);
  const giftProducts = new Set(config.giftProducts.map(String));
  let tail = Promise.resolve();
  let lastError = null;
  let lastFocusSync = 0;
  let checkoutBusy = false;
  let allowSubmit = false;
  function previewUrl(input) {
    const url = new URL(String(input), location.href);
    if (config.previewTheme) url.searchParams.set('preview_theme_id', config.previewTheme);
    return url;
  }
  const enqueue = task => {
    const result = tail.then(task, task);
    tail = result.catch(() => {});
    return result;
  };
  function report(error) {
    lastError = error;
    const target = document.querySelector('[data-mfvg-error]');
    if (target) { target.textContent = config.error; target.hidden = false; }
  }
  function clearError() {
    lastError = null;
    const target = document.querySelector('[data-mfvg-error]');
    if (target) target.hidden = true;
  }
  async function api(action, body) {
    const response = await rawFetch(`${root()}cart${action ? `/${action}` : ''}.js`, body === undefined
      ? { credentials: 'same-origin', cache: 'no-store' }
      : { method: 'POST', credentials: 'same-origin', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
    const data = await response.json();
    if (!response.ok || data.errors || data.status >= 400) throw new Error(data.description || config.error);
    return data;
  }
  function eligibleIds(cart) {
    if (!config.allProducts) return config.eligibleIds;
    return [...new Set(cart.items.filter(item => !item.gift_card && !giftProducts.has(String(item.product_id))).map(item => item.product_id))];
  }
  async function reconcile() {
    if (!config.valid || giftIds.length !== 2 || new Set(giftIds).size !== 2) throw new Error(config.error);
    let cart = await api('');
    let changed = false;
    // Bounded retries allow inventory/cart constraints to fail visibly, rather than looping.
    for (let attempt = 0; attempt < 3; attempt++) {
      const plan = window.MFVGiftRules.planCart(cart.items, eligibleIds(cart), config.giftVariants);
      if (!Object.keys(plan.updates).length && !plan.additions.length) {
        clearError();
        return { cart, changed };
      }
      if (Object.keys(plan.updates).length) await api('update', { updates: plan.updates });
      // Read again after removals; line keys and gift entitlements may have changed.
      cart = await api('');
      const fresh = window.MFVGiftRules.planCart(cart.items, eligibleIds(cart), config.giftVariants);
      if (fresh.additions.length) await api('add', { items: fresh.additions });
      changed = true;
      cart = await api('');
    }
    throw new Error(config.error);
  }
  async function sections(ids) {
    if (!ids.length) return {};
    const url = previewUrl(location.href);
    url.searchParams.set('sections', ids.slice(0, 5).join(','));
    const response = await rawFetch(url, { credentials: 'same-origin', cache: 'no-store' });
    if (!response.ok) throw new Error(config.error);
    const html = await response.json();
    if (ids.some(id => !html[id])) throw new Error(config.error);
    return html;
  }
  function descriptors() {
    const result = [];
    if (document.querySelector('cart-drawer #CartDrawer')) result.push({ id: 'cart-drawer', target: 'cart-drawer #CartDrawer', source: '#CartDrawer' });
    if (document.querySelector('#cart-icon-bubble')) result.push({ id: 'cart-icon-bubble', target: '#cart-icon-bubble', source: '.shopify-section' });
    for (const id of ['main-cart-items', 'main-cart-footer']) {
      const section = document.getElementById(id);
      if (section?.dataset.id) result.push({ id: section.dataset.id, target: `#${id} .js-contents`, source: '.js-contents' });
    }
    return result;
  }
  async function refresh(cart) {
    const list = descriptors();
    const html = await sections(list.map(item => item.id));
    for (const descriptor of list) {
      const target = document.querySelector(descriptor.target);
      const source = new DOMParser().parseFromString(html[descriptor.id], 'text/html').querySelector(descriptor.source);
      if (target && source) target.innerHTML = source.innerHTML;
    }
    const drawer = document.querySelector('cart-drawer');
    drawer?.classList.toggle('is-empty', cart.item_count === 0);
    drawer?.querySelector('#CartDrawer-Overlay')?.addEventListener('click', () => drawer.close());
    document.querySelector('cart-items')?.classList.toggle('is-empty', cart.item_count === 0);
    document.querySelector('#main-cart-footer')?.classList.toggle('is-empty', cart.item_count === 0);
  }
  // Only same-origin Shopify cart POSTs are intercepted. Other requests are untouched.
  window.fetch = function(input, init) {
    const url = new URL(input instanceof Request ? input.url : String(input), location.href);
    const method = String(init?.method || (input instanceof Request ? input.method : 'GET')).toUpperCase();
    const match = url.pathname.match(/\/cart\/(add|change|update|clear)(?:\.js)?\/?$/);
    if (config.previewTheme && method === 'GET' && url.origin === location.origin && (url.searchParams.has('sections') || url.searchParams.has('section_id'))) {
      const forced = previewUrl(url);
      input = input instanceof Request ? new Request(forced, input) : forced;
    }
    if (url.origin !== location.origin || method !== 'POST' || !match) return rawFetch(input, init);
    return enqueue(async () => {
      const response = await rawFetch(input, init);
      if (!response.ok) return response;
      let original;
      try { original = await response.clone().json(); } catch { return response; }
      if (original.errors || original.status >= 400) return response;
      try {
        const result = await reconcile();
        const requested = Object.keys(original.sections || {});
        if (!result.changed && !(config.previewTheme && requested.length)) return response;
        const rendered = requested.length ? await sections(requested) : undefined;
        const data = match[1] === 'add' ? { ...original } : { ...result.cart };
        if (rendered) data.sections = rendered;
        const headers = new Headers(response.headers);
        headers.delete('content-length'); headers.delete('content-encoding');
        headers.set('Content-Type', 'application/json; charset=utf-8');
        return new Response(JSON.stringify(data), { status: response.status, statusText: response.statusText, headers });
      } catch (error) {
        // A paid addition is not repeated if a gift request fails; retry is offered at checkout.
        report(error);
        return response;
      }
    });
  };
  async function syncVisibleCart() {
    const host = document.querySelector('cart-drawer.active') || document.querySelector('cart-items');
    let cart = await api('');
    const updates = {};
    host?.querySelectorAll('input[name="updates[]"]').forEach(input => {
      const line = cart.items.find(item => item.key === input.dataset.quantityLineKey);
      const quantity = Number(input.value);
      if (line && !giftIds.includes(String(line.variant_id)) && Number.isSafeInteger(quantity) && quantity >= 0 && quantity !== line.quantity) updates[line.key] = quantity;
    });
    const note = host?.querySelector('[name="note"]') || document.querySelector('cart-note [name="note"]');
    const body = {};
    if (Object.keys(updates).length) body.updates = updates;
    if (note && note.value !== (cart.note || '')) body.note = note.value;
    if (Object.keys(body).length) await api('update', body);
    return reconcile();
  }
  async function checkout(target, form) {
    if (checkoutBusy) return;
    checkoutBusy = true;
    try {
      const result = await enqueue(syncVisibleCart);
      if (result.changed) await refresh(result.cart);
      clearError();
      if (form) {
        // Quantities were flushed by line key. Posting paid-only arrays would target gift indexes.
        const currentForm = document.getElementById(form.id) || form;
        const currentButton = currentForm.querySelector('[name="checkout"]') || document.querySelector(`[form="${currentForm.id}"][name="checkout"]`) || target;
        currentForm.addEventListener('formdata', event => event.formData.delete('updates[]'), { once: true });
        allowSubmit = true;
        try { currentForm.requestSubmit(currentButton); } finally { allowSubmit = false; }
      } else location.assign(target.href);
    } catch (error) { report(error); }
    finally { checkoutBusy = false; }
  }
  document.addEventListener('submit', event => {
    if (allowSubmit || event.submitter?.name !== 'checkout') return;
    event.preventDefault(); event.stopImmediatePropagation();
    checkout(event.submitter, event.target);
  }, true);
  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link || new URL(link.href, location.href).origin !== location.origin || !/\/(?:checkouts?|checkout\/)/.test(new URL(link.href).pathname)) return;
    event.preventDefault(); event.stopImmediatePropagation(); checkout(link);
  }, true);
  async function startup() {
    try { await enqueue(async () => { const result = await reconcile(); if (result.changed) await refresh(result.cart); }); }
    catch (error) { report(error); }
  }
  window.addEventListener('pageshow', event => { if (event.persisted) startup(); });
  window.addEventListener('focus', () => { if (Date.now() - lastFocusSync > 5000) { lastFocusSync = Date.now(); startup(); } });
  window.MFVSurpriseCart = { sync: () => enqueue(reconcile), ready: () => tail, hasError: () => !!lastError };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', startup, { once: true });
  else startup();
})();
